import { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Heading,
  Text,
  Spinner,
  Alert,
  AlertIcon,
  Badge,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  VStack,
  HStack,
  Divider,
  Center,
  Select,
  IconButton,
  useColorMode,
  useColorModeValue,
  Input
} from '@chakra-ui/react';
import { MoonIcon, SunIcon } from '@chakra-ui/icons';
import Dashboard from './components/Dashboard';
import RepoDetail from './components/RepoDetail';
import BuildDetail from './components/BuildDetail';
import DependencyRow from './components/DependencyRow';

function RepoDetailWrapper() {
  const { repository } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    const guidRegex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
    if (guidRegex.test(repository)) {
      fetch('/api/repos')
        .then(res => res.json())
        .then(repos => {
          const found = repos.find(r => r.id === repository);
          if (found && found.repository) {
            navigate(`/repo/${found.repository}`, { replace: true });
          }
        });
    }
  }, [repository, navigate]);

  return <RepoDetail repository={repository} />;
}

function BuildDetailWrapper() {
  const { repository, buildId } = useParams();
  return <BuildDetailPage repository={repository} buildId={buildId} />;
}

function BuildDetailPage({ repository, buildId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetch(`/api/repos/${repository}`)
      .then((res) => {
        if (!res.ok) throw new Error('Failed to fetch repo details');
        return res.json();
      })
      .then((d) => {
        setData(d);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [repository]);

  if (loading) return <Center minH="60vh"><Spinner size="xl" /></Center>;
  if (error) return <Alert status="error" mt={10}><AlertIcon />Error: {error}</Alert>;
  if (!data) return null;

  const filesArr = data.artifacts[buildId] || [];
  // Find highest version in this build
  const getHighestVersionFiles = (filesArr) => {
    if (!filesArr || filesArr.length === 0) return [];
    const versions = [...new Set(filesArr.map(f => f.M42WSMVersion).filter(Boolean))].sort((a, b) => (b || '').localeCompare(a || '', undefined, { numeric: true, sensitivity: 'base' }));
    if (versions.length === 0) return filesArr;
    const highest = versions[0];
    return filesArr.filter(f => f.M42WSMVersion === highest);
  };
  const highestFiles = getHighestVersionFiles(filesArr);

  return (
    <Box maxW="900px" mx="auto" mt={10} p={4}>
      <Button as={Link} to={`/repo/${repository}`} mb={4} colorScheme="teal" variant="outline">← Back to Repo</Button>
      <BuildDetail repository={repository} buildId={buildId} files={highestFiles} />
    </Box>
  );
}

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/repo/:repository" element={<RepoDetailWrapper />} />
        <Route path="/repo/:repository/build/:buildId" element={<BuildDetailWrapper />} />
      </Routes>
    </Router>
  );
}

export default App;
